import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RiUserAddLine, RiMoneyDollarCircleLine, RiMessageLine } from 'react-icons/ri';

/* ─────────────────────────────────────────────────────────────
   COLOR PALETTE
───────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F7FF",
  softBlue: "#EBF1FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#0F1F3D",
  textMid: "#374966",
  textMuted: "#6B7FA3",
  border: "#D6E4FF",
  borderLight: "#EBF1FF",
  shadow: "0 4px 24px rgba(26,86,219,0.08)",
  shadowMd: "0 8px 40px rgba(26,86,219,0.12)",
  shadowLg: "0 20px 60px rgba(26,86,219,0.16)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EBF1FF 0%, #F4F7FF 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

const Overview = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPayments: 0,
    totalMessages: 0,
    revenue: 0
  });

  useEffect(() => {
    // Use mock data since API might not be available yet
    setStats({
      totalUsers: 1523,
      totalPayments: 892,
      totalMessages: 45,
      revenue: 245600
    });
  }, []);

  const statCards = [
    {
      title: 'Total Registrations',
      value: (stats.totalUsers || 0).toLocaleString(),
      icon: RiUserAddLine,
      gradient: T.gradPrimary,
      bgGradient: T.gradSoft
    },
    {
      title: 'Total Revenue',
      value: '$' + (stats.revenue || 0).toLocaleString(),
      icon: RiMoneyDollarCircleLine,
      gradient: T.gradGold,
      bgGradient: T.gradSoft
    },
    {
      title: 'Pending Messages',
      value: (stats.totalMessages || 0).toLocaleString(),
      icon: RiMessageLine,
      gradient: T.gradNavy,
      bgGradient: T.gradSoft
    }
  ];

  // Recent activity mock data
  const recentActivities = [
    { action: 'New user registration', time: '2 minutes ago', user: 'John Doe' },
    { action: 'Payment received', time: '15 minutes ago', user: 'Sarah Smith' },
    { action: 'New message from support', time: '1 hour ago', user: 'Mike Johnson' },
    { action: 'User upgraded to premium', time: '3 hours ago', user: 'Emily Brown' },
    { action: 'New summit registration', time: '5 hours ago', user: 'David Wilson' },
  ];

  return (
    <div>
      <h1 style={{ 
        fontSize: '1.875rem', 
        fontWeight: 'bold', 
        color: T.navy,
        marginBottom: '2rem' 
      }}>
        Dashboard Overview
      </h1>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr)',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            style={{
              background: T.white,
              borderRadius: '1rem',
              padding: '1.5rem',
              boxShadow: T.shadowMd,
              border: `1px solid ${T.borderLight}`,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: 'pointer',
            }}
            whileHover={{ 
              y: -4,
              boxShadow: T.shadowLg,
              transition: { duration: 0.2 }
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem'
            }}>
              <div style={{
                padding: '0.75rem',
                borderRadius: '0.75rem',
                background: stat.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <stat.icon style={{ fontSize: '1.5rem', color: T.white }} />
              </div>
              <span style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: T.navy
              }}>
                {stat.value}
              </span>
            </div>
            <h3 style={{
              color: T.textMuted,
              fontSize: '0.875rem',
              fontWeight: 500,
              margin: 0
            }}>
              {stat.title}
            </h3>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        style={{
          background: T.white,
          borderRadius: '1rem',
          padding: '1.5rem',
          boxShadow: T.shadowMd,
          border: `1px solid ${T.borderLight}`,
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: T.navy,
            margin: 0
          }}>
            Recent Activity
          </h2>
          <button style={{
            background: T.gradPrimary,
            color: T.white,
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            View All
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {recentActivities.map((activity, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + (idx * 0.05) }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 0',
                borderBottom: `1px solid ${T.borderLight}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                <div style={{
                  width: '0.5rem',
                  height: '0.5rem',
                  backgroundColor: T.accentGold,
                  borderRadius: '50%',
                }}></div>
                <div>
                  <div style={{ color: T.text, fontWeight: 500, marginBottom: '0.25rem' }}>
                    {activity.action}
                  </div>
                  <div style={{ color: T.textMuted, fontSize: '0.75rem' }}>
                    {activity.user}
                  </div>
                </div>
              </div>
              <span style={{
                color: T.textMuted,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                marginLeft: '1rem'
              }}>
                {activity.time}
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Optional: Stats Summary Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        style={{
          marginTop: '1.5rem',
          padding: '1rem 1.5rem',
          background: T.gradSoft,
          borderRadius: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '0.5rem',
            height: '0.5rem',
            backgroundColor: T.primary,
            borderRadius: '50%',
          }}></div>
          <span style={{ color: T.textMid, fontSize: '0.875rem' }}>
            Total Registrations: <strong style={{ color: T.navy }}>{stats.totalUsers.toLocaleString()}</strong>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '0.5rem',
            height: '0.5rem',
            backgroundColor: T.accentGold,
            borderRadius: '50%',
          }}></div>
          <span style={{ color: T.textMid, fontSize: '0.875rem' }}>
            Total Payments: <strong style={{ color: T.navy }}>${stats.revenue.toLocaleString()}</strong>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '0.5rem',
            height: '0.5rem',
            backgroundColor: T.accent,
            borderRadius: '50%',
          }}></div>
          <span style={{ color: T.textMid, fontSize: '0.875rem' }}>
            Success Rate: <strong style={{ color: T.navy }}>92.5%</strong>
          </span>
        </div>
      </motion.div>
    </div>
  );
};

export default Overview;